import React from 'react';
import {
  FileText, ShieldCheck, MapPin, Flag, HelpCircle,
  Check, ExternalLink, Truck, PackageCheck,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';

/**
 * Sidebar de résumé fixe à droite du wizard.
 *
 * Selon le type (loading / unloading), affiche des cartes différentes.
 *  - Commun : "Résumé de l'opération"
 *  - Loading : "Contrôles avant départ" + "Localisation GPS"
 *  - Unloading : "Réception" + "Prochaines étapes"
 */
const WizardSidebar = ({ note, operation, type, currentStep, totalSteps, gps, operatorName, stepLabels = [] }) => {
  const isLoading = type === 'loading';
  const ref = note?.noteNumber || operation?.reference || '—';

  return (
    <aside className="space-y-4" data-testid={`wizard-sidebar-${type}`}>
      {/* Bloc 1 : Résumé de l'opération (commun) */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-emerald-700">
            <FileText className="w-4 h-4" />
            <h3 className="text-sm font-semibold">Résumé de l'opération</h3>
          </div>
          <div className="space-y-2 text-xs">
            <Row label="Référence" value={<span className="font-mono">{ref}</span>} />
            <Row label="Client" value={operation?.clientName || '—'} />
            <Row
              label="Trajet"
              value={
                <span className="text-right">
                  {operation?.departure || '—'}
                  <br />
                  <span className="text-slate-400">→</span> {operation?.arrival || '—'}
                </span>
              }
            />
            <Row label="Marchandise" value={`${operation?.merchandiseType || '—'}${operation?.weight ? ` (${operation.weight} T)` : ''}`} />
            <Row label="Véhicule" value={operation?.vehicleType || '—'} />
          </div>
        </CardContent>
      </Card>

      {/* Bloc 2 : selon type */}
      {isLoading ? (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700">
              <ShieldCheck className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Contrôles avant départ</h3>
            </div>
            <div className="space-y-2 text-xs">
              <CheckRow label="Conducteur identifié" value={operation?.driverName || 'Non identifié'} ok={!!operation?.driverName} />
              <CheckRow label="Véhicule vérifié" value={operation?.vehicleType || 'Non spécifié'} ok={!!operation?.vehicleType} />
              <CheckRow label="Documents obligatoires" value="OK" ok={true} />
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700">
              <PackageCheck className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Réception</h3>
            </div>
            <div className="space-y-2 text-xs">
              <CheckRow label="Destinataire" value={operatorName || operation?.driverName || '—'} ok={!!operatorName} />
              {gps && (
                <CheckRow
                  label="Position GPS captée"
                  value={`${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)}`}
                  ok={true}
                />
              )}
              <CheckRow
                label="Date et heure"
                value={new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                ok={true}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bloc 3 : Loading → Localisation GPS / Unloading → Prochaines étapes */}
      {isLoading ? (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700">
              <MapPin className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Localisation GPS</h3>
            </div>
            {gps ? (
              <div className="text-xs space-y-1">
                <div className="text-slate-500">Position captée</div>
                <div className="font-mono text-slate-700 text-[11px]">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>
                <div className="mt-2 h-24 bg-emerald-50 rounded border border-emerald-100 flex items-center justify-center relative overflow-hidden">
                  <MapPin className="w-8 h-8 text-emerald-600" />
                  <a
                    href={`https://www.google.com/maps?q=${gps.lat},${gps.lng}`}
                    target="_blank" rel="noreferrer"
                    className="absolute bottom-1 right-1 bg-white/80 rounded px-1 py-0.5 text-[10px] text-emerald-700 hover:bg-white"
                  >
                    <ExternalLink className="inline w-2.5 h-2.5 mr-0.5" /> Maps
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-400 italic">GPS non capté</div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-emerald-700">
              <Flag className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Prochaines étapes</h3>
            </div>
            <div className="space-y-2.5">
              {stepLabels.map((label, i) => {
                const current = i === currentStep;
                const done = i < currentStep;
                return (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0
                      ${current ? 'bg-emerald-600 text-white' : done ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' : 'bg-white border border-slate-300 text-slate-400'}
                    `}>
                      {done ? <Check className="w-3 h-3" /> : (i + 1)}
                    </div>
                    <div className={`leading-tight ${current ? 'text-emerald-700' : done ? 'text-slate-600' : 'text-slate-400'}`}>
                      <div className="font-medium">{label}</div>
                      {current && <div className="text-emerald-600 text-[10px]">Vous êtes ici</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aide bas (commun, optionnel) */}
      <div className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
        <HelpCircle className="w-3 h-3" />
        Besoin d'aide ? Contactez votre administrateur.
      </div>
    </aside>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-start gap-2">
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="text-slate-800 text-right">{value}</span>
  </div>
);

const CheckRow = ({ label, value, ok }) => (
  <div className="flex items-start gap-2">
    <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${ok ? 'text-emerald-600' : 'text-slate-300'}`} />
    <div className="flex-1 flex justify-between items-start gap-2">
      <span className="text-slate-600">{label}</span>
      <span className="text-slate-800 text-right text-[11px]">{value}</span>
    </div>
  </div>
);

export default WizardSidebar;
